import { c as _c } from "react/compiler-runtime";
import { useId } from 'react';
import { type FieldValues, type Path, type RegisterOptions, useFormContext, useFormState } from 'react-hook-form';
import type { CheckboxProps } from '../../checkbox';
import Checkbox from '../../checkbox';
import type { LabelTooltipProps } from '../../label';
import FormWrapper from '../internals/form-wrapper';
export type FormCheckboxOption = {
  label: string | React.ReactNode;
  value: string;
  hint?: string;
};
export type FormCheckboxOptions = FormCheckboxOption[];
export type FormCheckboxGroupProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  options: FormCheckboxOptions;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  errorPosition?: 'absolute' | 'relative';
  orientation?: 'vertical' | 'horizontal';
  addon?: React.ReactNode;
  tooltip?: LabelTooltipProps;
  className?: string;
  hideError?: boolean;
} & Omit<CheckboxProps, 'checked' | 'defaultChecked' | 'name' | 'onBlur' | 'ref' | 'label' | 'hasError' | 'hint' | 'value'>;
export default function FormCheckbox(t0) {
  const $ = _c(53);
  let addon;
  let baseId;
  let checkboxProps;
  let className;
  let hideError;
  let name;
  let onChange;
  let options;
  let rules;
  let t1;
  let t2;
  let tooltip;
  if ($[0] !== t0) {
    ({
      name,
      options,
      rules,
      id: baseId,
      errorPosition: t1,
      orientation: t2,
      addon,
      onChange,
      tooltip,
      className,
      hideError,
      ...checkboxProps
    } = t0);
    $[0] = t0;
    $[1] = addon;
    $[2] = baseId;
    $[3] = checkboxProps;
    $[4] = className;
    $[5] = hideError;
    $[6] = name;
    $[7] = onChange;
    $[8] = options;
    $[9] = rules;
    $[10] = t1;
    $[11] = t2;
    $[12] = tooltip;
  } else {
    addon = $[1];
    baseId = $[2];
    checkboxProps = $[3];
    className = $[4];
    hideError = $[5];
    name = $[6];
    onChange = $[7];
    options = $[8];
    rules = $[9];
    t1 = $[10];
    t2 = $[11];
    tooltip = $[12];
  }
  const errorPosition = t1 === undefined ? "relative" : t1;
  const orientation = t2 === undefined ? "vertical" : t2;
  const {
    register,
    control,
    getFieldState
  } = useFormContext();
  let t3;
  if ($[13] !== checkboxProps.disabled || $[14] !== control || $[15] !== name) {
    t3 = {
      control,
      name,
      disabled: checkboxProps.disabled
    };
    $[13] = checkboxProps.disabled;
    $[14] = control;
    $[15] = name;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const formState = useFormState(t3);
  let t4;
  if ($[17] !== formState || $[18] !== getFieldState || $[19] !== name) {
    t4 = getFieldState(name, formState);
    $[17] = formState;
    $[18] = getFieldState;
    $[19] = name;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const {
    error
  } = t4;
  const contextId = useId();
  const id = baseId || contextId;
  const t5 = error?.message;
  const t6 = formState.disabled || checkboxProps.disabled;
  let t7;
  if ($[21] !== checkboxProps || $[22] !== error || $[23] !== formState.disabled || $[24] !== id || $[25] !== name || $[26] !== onChange || $[27] !== options || $[28] !== register || $[29] !== rules) {
    let t8;
    if ($[31] !== checkboxProps || $[32] !== error || $[33] !== formState.disabled || $[34] !== id || $[35] !== name || $[36] !== onChange || $[37] !== register || $[38] !== rules) {
      t8 = t9 => {
        const {
          label,
          value,
          hint
        } = t9;
        const {
          onChange: reactHookFormOnChange,
          ...rest
        } = register(name, {
          ...rules
        });
        return <Checkbox key={value} {...checkboxProps} {...rest} onChange={e => {
          reactHookFormOnChange(e);
          onChange?.(e);
        }} disabled={formState.disabled || checkboxProps.disabled} hasError={!!error} id={`${id}-${value}`} data-dd-privacy="mask" label={label} hint={hint} value={value} />;
      };
      $[31] = checkboxProps;
      $[32] = error;
      $[33] = formState.disabled;
      $[34] = id;
      $[35] = name;
      $[36] = onChange;
      $[37] = register;
      $[38] = rules;
      $[39] = t8;
    } else {
      t8 = $[39];
    }
    t7 = options.map(t8);
    $[21] = checkboxProps;
    $[22] = error;
    $[23] = formState.disabled;
    $[24] = id;
    $[25] = name;
    $[26] = onChange;
    $[27] = options;
    $[28] = register;
    $[29] = rules;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[40] !== className || $[41] !== t7) {
    t8 = <div className={className}>{t7}</div>;
    $[40] = className;
    $[41] = t7;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  let t9;
  if ($[43] !== addon || $[44] !== errorPosition || $[45] !== hideError || $[46] !== id || $[47] !== orientation || $[48] !== t5 || $[49] !== t6 || $[50] !== t8 || $[51] !== tooltip) {
    t9 = <FormWrapper id={id} error={t5} errorPosition={errorPosition} addon={addon} size="default" orientation={orientation} fullWidth={true} disabled={t6} tooltip={tooltip} hideError={hideError}>{t8}</FormWrapper>;
    $[43] = addon;
    $[44] = errorPosition;
    $[45] = hideError;
    $[46] = id;
    $[47] = orientation;
    $[48] = t5;
    $[49] = t6;
    $[50] = t8;
    $[51] = tooltip;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  return t9;
}